import TransparentCharacterImage from './TransparentCharacterImage';
import { isPraamis } from '../lib/praamisIdentity';
import {
  BookOpen,
  Cat,
  Check,
  CircleUserRound,
  PawPrint,
  Bot,
  Save,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {robotVariants, type RobotCategory} from '../character/robotGallery';
import {
  characterFamilyKey,
  genderOptionsForFamily,
  libraryAnimalSpeciesOptions,
  manifestOptions,
  runtimeDeviceOptions,
  toneOptionsForFamily,
} from '../character/manifest';
import CharacterPreview from './CharacterPreview';
import './NicknameStudio.css';
import AgentCharacterAvatar from './AgentCharacterAvatar';
import { BOOKWORM_OUTFITS, BOOKWORM_GLASSES, bookwormOutfit, bookwormGlassesOn } from './BookwormApprovedArt';
import CharacterPreviewBoundary from './CharacterPreviewBoundary';
import OwlApprovalSheet from './OwlApprovalSheet';
import {updateEmployeeNickname,saveEmployeeCharacterProfile} from '../lib/ownerControls';
import type {
  Agent,
  CharacterAppearance,
  CharacterBaseType,
  CharacterGender,
  CharacterProfile,
  CharacterPronouns,
} from '../types';

type Props = {
  workspaceId: string;
  agents: Agent[];
  profiles: CharacterProfile[];
  selectedAgentId: string;
  onSelectAgent: (id: string) => void;
  onProfileSaved: (profile: CharacterProfile) => void;
  onAgentSaved: (agent: Agent) => void;
  /** Public staging design only; save into local preview state, not the database. */
  previewOnly?: boolean;
};

type AppearanceKey = keyof CharacterAppearance;

type AppearanceField = {
  key: AppearanceKey;
  label: string;
  note: string;
  options: string[];
};

const baseOptions: Array<{
  value: CharacterBaseType;
  label: string;
  note: string;
  icon: typeof PawPrint;
}> = [
  { value: 'default_agent', label: 'AgentOrdinary', note: 'Official robot family · customizable colors', icon: Bot },
  { value: 'bear', label: 'Bear', note: 'AgentOrdinary bear characters', icon: PawPrint },
  { value: 'human', label: 'Human', note: 'People and human-style agents', icon: CircleUserRound },
  { value: 'bookworm', label: 'Bookworm', note: 'Bookish and scholarly characters', icon: BookOpen },
  { value: 'library_animal', label: 'Owl', note: 'Approved Owl character', icon: Cat },
];

/** The backend system handle is intentionally never printed in Character Studio. */
function visibleEmployeeName(agent:Agent,publicPreview=false):string {
 return publicPreview?agent.title:(agent.nickname?.trim()||agent.official_name||agent.title);
}
const robotColorChoices = [
 {name:'Green',css:'#66e68a',filter:'none'},
 {name:'Aqua',css:'#42e1d9',filter:'hue-rotate(55deg)'},
 {name:'Blue',css:'#55b9ff',filter:'hue-rotate(125deg)'},
 {name:'Pink',css:'#ff9cc8',filter:'hue-rotate(270deg)'},
 {name:'Purple',css:'#bb9eff',filter:'hue-rotate(205deg)'},
 {name:'Orange',css:'#ffb061',filter:'hue-rotate(315deg)'},
 {name:'Red',css:'#fa788c',filter:'hue-rotate(280deg) saturate(1.3)'},
 {name:'Monochrome',css:'#b7c3d0',filter:'grayscale(1)'}
] as const;

const pronounsByGender: Record<CharacterGender, CharacterPronouns> = {
  Feminine: 'she/her',
  Masculine: 'he/him',
};

const identityFields: AppearanceField[] = [
  {
    key: 'gender',
    label: 'Gender presentation',
    note: 'Controls the visual body variant only. It does not change the employee role or behavior.',
    options: [''],
  },
  {
    key: 'pronouns',
    label: 'Pronouns',
    note: 'Defaults from gender presentation and can be overridden independently.',
    options: ['', 'she/her', 'he/him'],
  },
];

const defaultRobotFields: AppearanceField[] = [
  { key: 'expression', label: 'Expression', note: 'The face also responds to live task activity.', options: ['Happy','Focused','Curious','Surprised','Thinking','Playful','Serious','Calm'] },
  { key: 'eyewear', label: 'Glasses', note: 'All four styles work with headphones and other accessories.', options: ['None','Round','Slim','Oversized','Visor'] },
  { key: 'audio', label: 'Headphones', note: 'One headphone design. Compatible with every glasses style.', options: ['None','Headphones'] },
  { key: 'lashes', label: 'Lashes', note: 'Optional facial detail.', options: ['Off','On'] },
  { key: 'blush', label: 'Pink cheeks', note: 'Can combine with lashes and glasses.', options: ['Off','On'] },
  { key: 'faceMark', label: 'Face mark', note: 'One face decal at a time.', options: ['None','Beauty Mark','Heart Decal'] },
  { key: 'backAccessory', label: 'Back accessory', note: 'One backpack, utility pack or pouch.', options: ['None','Backpack','Utility Pack','Messenger Pouch'] },
  { key: 'prop', label: 'Handheld work item', note: 'Existing translucent tablet, phone, laptop, or another approved item.', options: ['None','Tablet','Phone','Laptop','Book','Clipboard','Stylus Pen','Wrench','Coffee Cup'] },
  { key: 'companion', label: 'Tech companion', note: 'Optional floating mini drone.', options: ['None','Mini Drone'] },
  { key: 'device', label: 'Working device', note: 'Shown during real work; does not start a task.', options: ['Tablet','Phone','Laptop'] },
  { key: 'idlePersonality', label: 'Idle personality', note: 'Presentation only; does not affect employee behavior.', options: ['Composed','Alert','Warm','Focused'] },
];

const commonFields: AppearanceField[] = [
  {
    key: 'outfit',
    label: 'Outfit',
    note: 'The character can keep multiple visual styles later; this is the active look.',
    options: [
      'Agent Ordinary Classic',
      'Executive',
      'Smart Casual',
      'Streetwear',
      'Creative',
      'Cozy',
      'Techwear',
    ],
  },
  {
    key: 'headwear',
    label: 'Headwear',
    note: 'Optional hat or head accessory.',
    options: ['None', 'Baseball cap', 'Beanie', 'Bucket hat', 'Headband'],
  },
  {
    key: 'eyewear',
    label: 'Eyewear',
    note: 'Standard glasses and smart-glasses options.',
    options: ['None', 'Clear glasses', 'Tinted glasses', 'Smart glasses'],
  },
  {
    key: 'audio',
    label: 'Audio',
    note: 'Personal audio accessory.',
    options: ['None', 'AirPods', 'Over-ear headphones', 'Studio headset'],
  },
  {
    key: 'jewelry',
    label: 'Jewelry',
    note: 'Optional finishing accessory.',
    options: ['None', 'Stud earrings', 'Hoop earrings', 'Chain', 'Watch'],
  },
  {
    key: 'prop',
    label: 'Prop',
    note: 'A movable character can later carry or put away this item.',
    options: ['None', 'Tablet', 'Notebook', 'Phone', 'Coffee'],
  },
  {
    key: 'device',
    label: 'Working device',
    note: 'Device shown automatically while this employee is working.',
    options: ['Phone', 'Tablet', 'Laptop'],
  },
  {
    key: 'idlePersonality',
    label: 'Idle personality',
    note: 'Controls only the character animation style while the employee is idle.',
    options: ['Composed', 'Alert', 'Warm', 'Focused'],
  },
];

const baseFields: Record<CharacterBaseType, AppearanceField[]> = {
  default_agent: [],
  human: [
    {
      key: 'hair',
      label: 'Hair',
      note: 'Includes the Black hairstyles requested for AgentOrdinary characters.',
      options: [
        'Curly afro',
        'Mini dreads',
        'Long dreads',
        'Two-strand twists',
        'Barrel twists',
        'Braids',
        'Loc bun',
        'Short curls',
        'Long curls',
        'Low fade',
        'Bald',
      ],
    },
    {
      key: 'skinTone',
      label: 'Skin tone',
      note: 'Choose the character skin tone independently from clothing and hair.',
      options: ['Deep', 'Rich brown', 'Warm brown', 'Medium brown', 'Light brown', 'Light'],
    },
  ],
  bear: [
    {
      key: 'furTone',
      label: 'Fur tone',
      note: 'Base fur color for the bear character.',
      options: ['Espresso', 'Classic brown', 'Honey', 'Cream', 'Black', 'Polar'],
    },
  ],
  bookworm: [],
  library_animal: [
    {
      key: 'species',
      label: 'Animal',
      note: 'Choose the library-animal character family.',
      options: ['Owl', 'Rabbit', 'Cat', 'Fox', 'Raccoon', 'Otter'],
    },
    {
      key: 'furTone',
      label: 'Fur / feather tone',
      note: 'Base coat or feather palette for the selected animal.',
      options: ['Natural', 'Dark', 'Warm', 'Golden', 'Cream', 'Snow'],
    },
  ],
};

function defaultAppearance(baseType: CharacterBaseType): CharacterAppearance {
  const common: CharacterAppearance = {
    outfit: 'Default body',
    headwear: 'None',
    eyewear: 'None',
    audio: 'None',
    jewelry: 'None',
    prop: 'None',
    device: 'Phone',
    idlePersonality: 'Focused',
    animationSpeed: 1,
  };

  if (baseType === 'default_agent') return { ...common, gender: 'Masculine', accentColor: 'Green', expression: 'Happy', eyewear: 'None', audio: 'None', lashes: 'Off', blush: 'Off', faceMark: 'None', backAccessory: 'None', prop: 'Tablet', companion: 'None', device: 'Tablet' };
  if (baseType === 'human') {
    return { ...common, hair: 'Curly afro', skinTone: 'Rich brown' };
  }
  if (baseType === 'bear') {
    return { ...common, furTone: 'Classic brown' };
  }
  if (baseType === 'bookworm') {
    return { ...common, bodyTone: 'Library green', outfit: 'Default body', eyewear: 'Off' };
  }
  return { ...common, species: 'Owl', furTone: 'Natural' };
}

function employeeAnimationDefaults(agent?: Agent): Pick<
  CharacterAppearance,
  'device' | 'idlePersonality' | 'animationSpeed'
> {
  const identity = [agent?.role_key, agent?.official_name, agent?.nickname]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return {
    device: 'Phone',
    idlePersonality: identity.includes('scout')
      ? 'Alert'
      : identity.includes('praamis')
        ? 'Composed'
        : 'Focused',
    animationSpeed: 1,
  };
}

function hydrateAppearance(
  baseType: CharacterBaseType,
  value?: CharacterAppearance,
): CharacterAppearance {
  const legacy = { ...(value ?? {}) } as Record<string, unknown>;

  if (legacy.gender === 'Neutral') delete legacy.gender;
  if (legacy.pronouns === 'they/them') delete legacy.pronouns;

  const hydrated = {
    ...defaultAppearance(baseType),
    ...legacy,
  } as CharacterAppearance;

  if (baseType === 'bookworm') {
    hydrated.bodyTone = 'Library green';
    hydrated.outfit = bookwormOutfit(hydrated.outfit);
    hydrated.eyewear = bookwormGlassesOn(hydrated.eyewear) ? 'On' : 'Off';
    // The approved art is self-contained; old generic accessories do not fit it.
    hydrated.headwear = 'None';
    hydrated.audio = 'None';
    hydrated.jewelry = 'None';
    hydrated.prop = 'None';
  }

  if (baseType === 'library_animal') {
    hydrated.outfit = 'Default body';
    if (!libraryAnimalSpeciesOptions().includes(hydrated.species || '')) {
      hydrated.species = 'Owl';
      hydrated.furTone = 'Natural';
    }
  }

  return hydrated;
}

function carryCommonAppearance(
  nextBase: CharacterBaseType,
  current: CharacterAppearance,
): CharacterAppearance {
  const next = defaultAppearance(nextBase);
  return {
    ...next,
    gender: current.gender ?? next.gender,
    pronouns: current.pronouns ?? next.pronouns,
    outfit: current.outfit ?? next.outfit,
    headwear: current.headwear ?? next.headwear,
    eyewear: current.eyewear ?? next.eyewear,
    audio: current.audio ?? next.audio,
    jewelry: current.jewelry ?? next.jewelry,
    prop: current.prop ?? next.prop,
    device: current.device ?? next.device,
    idlePersonality: current.idlePersonality ?? next.idlePersonality,
    animationSpeed: current.animationSpeed ?? next.animationSpeed,
    accentColor: current.accentColor ?? next.accentColor,
    expression: current.expression ?? next.expression,
    lashes: current.lashes ?? next.lashes,
    blush: current.blush ?? next.blush,
    faceMark: current.faceMark ?? next.faceMark,
    backAccessory: current.backAccessory ?? next.backAccessory,
    companion: current.companion ?? next.companion,
  };
}

export default function CharacterStudio({
  workspaceId,
  agents,
  profiles,
  selectedAgentId,
  onSelectAgent,
  onProfileSaved,
  onAgentSaved,
  previewOnly = false,
}: Props) {
  const selectedAgent = agents.find(agent => agent.id === selectedAgentId);
  const savedProfile = profiles.find(
    profile => profile.agent_id === selectedAgentId,
  );
  const initialBase = savedProfile?.base_type ?? 'default_agent';
  const [baseType, setBaseType] = useState<CharacterBaseType>(initialBase);
  const [appearance, setAppearance] = useState<CharacterAppearance>(() => {
    const hydrated = hydrateAppearance(initialBase, savedProfile?.appearance);
    return savedProfile
      ? hydrated
      : { ...hydrated, ...employeeAnimationDefaults(selectedAgent) };
  });
  const [saving, setSaving] = useState(false);
  const [nickname, setNickname] = useState(selectedAgent?.nickname || '');
  const [savingNickname, setSavingNickname] = useState(false);
  const [previewActivity, setPreviewActivity] = useState<'idle'|'working'>('idle');
  const [robotGalleryReady,setRobotGalleryReady] = useState(false);
  const [robotCategory,setRobotCategory] = useState<RobotCategory>(() => {
    const existing=robotVariants.find(item=>item.id===savedProfile?.appearance?.robotVariantId);
    return existing?.category||'agents';
  });
  useEffect(()=>{
    // Do not offer missing assets: Character Studio can show the original 20 after
    // the individually supplied high-resolution files are installed in staging.
    let valid=true;
    const probe=new Image();
    probe.onload=()=>{if(valid)setRobotGalleryReady(true)};
    probe.onerror=()=>{if(valid)setRobotGalleryReady(false)};
    probe.src=robotVariants[0].src;
    return ()=>{valid=false};
  },[]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const nextBase = savedProfile?.base_type ?? 'default_agent';
    setBaseType(nextBase);
    const hydrated = hydrateAppearance(nextBase, savedProfile?.appearance);
    setAppearance(
      savedProfile
        ? hydrated
        : { ...hydrated, ...employeeAnimationDefaults(selectedAgent) },
    );
    const savedVariant=robotVariants.find(item=>item.id===savedProfile?.appearance?.robotVariantId);
    setRobotCategory(savedVariant?.category||'agents');
    setNickname(selectedAgent?.nickname || '');
    setPreviewActivity('idle');
    setNotice('');
    setError('');
  }, [selectedAgentId, selectedAgent?.nickname, savedProfile?.base_type, savedProfile?.updated_at]);

  const fields = useMemo(() => {
    const family = characterFamilyKey(baseType, appearance);

    const genderOptions = genderOptionsForFamily(family);
    const identity = identityFields
      .filter(field =>
        (baseType !== 'bookworm' || field.key !== 'gender') &&
        (field.key !== 'gender' || genderOptions.length > 0),
      )
      .map(field =>
        field.key === 'gender'
          ? { ...field, options: ['', ...genderOptions] }
          : field,
      );

    if (baseType === 'default_agent') return [...identity, ...defaultRobotFields];
    const base = baseFields[baseType].map(field => {
      if (field.key === 'species') {
        return { ...field, options: libraryAnimalSpeciesOptions() };
      }
      if (field.key === 'hair') {
        return { ...field, options: manifestOptions('human', 'hair') };
      }
      if (
        field.key === 'skinTone' ||
        field.key === 'furTone' ||
        field.key === 'bodyTone'
      ) {
        return { ...field, options: toneOptionsForFamily(family) };
      }
      return field;
    });

    const common = commonFields
      .filter(field => {
        if (baseType === 'bookworm') {
          return ['outfit', 'eyewear', 'idlePersonality'].includes(field.key);
        }
        return baseType !== 'library_animal' || field.key !== 'outfit';
      })
      .map(field => {
      if (baseType === 'bookworm' && field.key === 'outfit') {
        return {
          ...field,
          note: 'Four separate Booky looks: plain, professional, VIP Club tee and cozy.',
          options: [...BOOKWORM_OUTFITS],
        };
      }
      if (baseType === 'bookworm' && field.key === 'eyewear') {
        return {
          ...field,
          label: 'Glasses',
          note: 'Every outfit has its own approved glasses-on and glasses-off version.',
          options: [...BOOKWORM_GLASSES],
        };
      }
      if (
        field.key === 'outfit' ||
        field.key === 'headwear' ||
        field.key === 'eyewear' ||
        field.key === 'audio' ||
        field.key === 'jewelry' ||
        field.key === 'prop'
      ) {
        return { ...field, options: manifestOptions(family, field.key) };
      }
      if (field.key === 'device') {
        return {
          ...field,
          options: runtimeDeviceOptions(family),
        };
      }
      return field;
    });

    return [...identity, ...base, ...common];
  }, [baseType, appearance.species]);
  const savedSnapshot = JSON.stringify({
    baseType: savedProfile?.base_type ?? null,
    appearance: savedProfile?.appearance ?? {},
  });
  const editorSnapshot = JSON.stringify({ baseType, appearance });
  const dirty = !savedProfile || savedSnapshot !== editorSnapshot;

  function chooseBase(nextBase: CharacterBaseType) {
    setBaseType(nextBase);
    setAppearance(current =>
      hydrateAppearance(nextBase, carryCommonAppearance(nextBase, current)),
    );
    setNotice('');
  }

  function setLayer(key: AppearanceKey, value: string) {
    setAppearance(current => {
      if (key === 'gender') {
        const previousGender = current.gender;
        const nextGender = value ? (value as CharacterGender) : undefined;
        const currentPronouns = current.pronouns;
        const followsPreviousDefault =
          Boolean(previousGender) &&
          currentPronouns === pronounsByGender[previousGender as CharacterGender];

        return {
          ...current,
          gender: nextGender,
          pronouns: nextGender
            ? followsPreviousDefault || !currentPronouns
              ? pronounsByGender[nextGender]
              : currentPronouns
            : followsPreviousDefault
              ? undefined
              : currentPronouns,
        };
      }

      if (key === 'pronouns') {
        return {
          ...current,
          pronouns: value ? (value as CharacterPronouns) : undefined,
        };
      }

      return { ...current, [key]: value };
    });
    setNotice('');
  }

  async function saveNickname() {
    if (!selectedAgent) return;
    const next = nickname.trim().replace(/\s+/g, ' ');
    if (next.length > 40) { setError('Nickname must be 40 characters or fewer.'); return; }
    if (next === (selectedAgent.nickname || '')) return;
    if (previewOnly) {
      onAgentSaved({...selectedAgent,nickname:next||null});
      setNotice(next?'Nickname saved on this device.':'Optional nickname cleared.');
      return;
    }
    setSavingNickname(true);setError('');setNotice('');
    try {
      const result = await updateEmployeeNickname(workspaceId,selectedAgent,next);
      const saved = {...selectedAgent,nickname:result||null,revision:selectedAgent.revision+1};
      onAgentSaved(saved);
      setNotice(next ? 'Nickname saved.' : 'Nickname cleared.');
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Nickname could not be saved.');
    } finally { setSavingNickname(false); }
  }

  async function save() {
    if (!selectedAgent || !dirty) return;
    setSaving(true);
    setError('');
    setNotice('');

    try {
      const profile = previewOnly ? {
        id: savedProfile?.id || 'visual-'+selectedAgent.id,
        workspace_id: workspaceId,
        agent_id: selectedAgent.id,
        base_type: baseType,
        transparent_background: true,
        status: 'draft' as const,
        appearance,
        created_at: savedProfile?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } : await saveEmployeeCharacterProfile(
        workspaceId,
        selectedAgent.id,
        baseType,
        appearance,
      );
      onProfileSaved(profile);
      setNotice(previewOnly ? 'Look saved on this device. Account sync requires a private signed-in session.' : 'Character look saved to your account.');
    } catch (problem) {
      setError(
        problem instanceof Error
          ? problem.message
          : 'Character look could not be saved.',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="page-stack character-full-studio">
      <section className="studio-hero">
        <div>
          <div className="eyebrow">CHARACTER STUDIO</div>
          <h1>Build each agent's look.</h1>
          <p>
            Choose a character family, appearance, clothing, and accessories for every employee.
          </p>
        </div>
        <Sparkles size={34} aria-hidden="true" />
        <select
          aria-label="Select an existing agent"
          value={selectedAgentId}
          onChange={event => onSelectAgent(event.target.value)}
        >
          {agents.map(agent => (
            <option key={agent.id} value={agent.id}>
              {visibleEmployeeName(agent,previewOnly)}
              {!previewOnly && agent.nickname ? ' · ' + agent.title : ''}
            </option>
          ))}
        </select>
      </section>

      {error && <div className="message error">{error}</div>}
      {notice && <div className="message success"><Check size={17} />{notice}</div>}

      {!selectedAgent ? (
        <section className="panel empty">No existing agent is available to customize.</section>
      ) : (
        <div className="studio-grid">
          <section className="panel roster-panel">
            <div className="title-row">
              <h2>Current agents</h2>
              <span className="count">{profiles.length}/{agents.length} linked</span>
            </div>

            <div className="agent-list">
              {agents.map(agent => {
                const profile = profiles.find(item => item.agent_id === agent.id);
                const option = baseOptions.find(
                  item => item.value === profile?.base_type,
                );
                return (
                  <button
                    key={agent.id}
                    className={'agent-row ' + (agent.id === selectedAgentId ? 'selected' : '')}
                    onClick={() => onSelectAgent(agent.id)}
                  >
                    <AgentCharacterAvatar name={visibleEmployeeName(agent,previewOnly)} roleKey={agent.role_key} profile={profile} size={46}/>
                    <span>
                      <strong>{visibleEmployeeName(agent,previewOnly)}</strong>
                      <small>
                        {agent.title}
                      </small>
                    </span>
                    {profile && <Check size={17} />}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="panel editor-panel">
            <div className="title-row">
              <div>
                <div className="eyebrow">ACTIVE LOOK</div>
                <h2>{visibleEmployeeName(selectedAgent,previewOnly)}</h2>
                {selectedAgent.nickname&&<p className="character-visible-title">{selectedAgent.title}</p>}
              </div>
              <span className="count">{savedProfile ? 'Linked' : 'Not linked'}</span>
            </div>

            <div className="studio-editor-layout">
              <div className="studio-controls">
                <div className="builder-block first-builder-block">
                  <div>
                    <h3>Base character family</h3>
                    <p className="muted">
                      This changes only the visual foundation. Your existing employees stay intact.
                    </p>
                  </div>
                  <div className="base-grid">
                    {baseOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.value}
                          className={'base-card ' + (baseType === option.value ? 'selected' : '')}
                          onClick={() => chooseBase(option.value)}
                        >
                          <Icon size={27} />
                          <strong>{option.label}</strong>
                          <small>{option.note}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {baseType === 'default_agent' && (
                  <div className="builder-block robot-design-block">
                              {baseType==='default_agent'&&robotGalleryReady&&(
            <div className="character-robot-gallery character-robot-portraits" aria-label="AgentOrdinary individual character designs">
              <div className="character-robot-gallery-title">
                <strong>AgentOrdinary characters</strong>
                <small>Choose a character, pet, or companion for this employee.</small>
              </div>
              <div className="character-robot-category-tabs" role="group" aria-label="AgentOrdinary character categories">
                {([
                  {id:'agents',label:'Agents',icon:<Bot size={15}/>},
                  {id:'pets',label:'Pets',icon:<PawPrint size={15}/>},
                  {id:'others',label:'Others',icon:<Sparkles size={15}/>},
                ] as const).map(group=>(
                  <button key={group.id} type="button" className={'character-robot-category-tab '+(robotCategory===group.id?'active':'')} aria-pressed={robotCategory===group.id} onClick={()=>setRobotCategory(group.id)}>
                    {group.icon}<span>{group.label}</span>
                  </button>
                ))}
              </div>
              <div className="character-robot-portrait-grid">
                {robotVariants.filter(item=>item.id!=='ao-20' && (item.category||'agents')===robotCategory).map(item=>{
                  const selected=appearance.robotVariantId===item.id||(appearance.robotVariantId==='ao-20'&&item.id==='ao-17');
                  return <button key={item.id} type="button" disabled={item.requiresBrandCleanup} className={'character-robot-portrait-choice '+(selected?'selected':'')} aria-pressed={selected} aria-label={item.title+(item.requiresBrandCleanup?', logo cleanup needed':'')} onClick={()=>setAppearance(current=>({...current,robotVariantId:item.id}))}>
                    <span className="character-robot-portrait-frame"><TransparentCharacterImage src={item.src} alt="" loading="lazy" draggable={false} /></span>
                    <span>{item.title}</span>
                    {item.requiresBrandCleanup&&<small>Logo cleanup pending</small>}
                  </button>;
                })}
              </div>
              {appearance.robotVariantId&&<button type="button" className="character-clear-robot" onClick={()=>setAppearance(current=>({...current,robotVariantId:undefined}))}>Use basic robot instead</button>}
            </div>
          )}
          {baseType==='default_agent'&&!appearance.robotVariantId&&(
            <div className="character-robot-gallery" aria-label="AgentOrdinary color variants">
              <div className="character-robot-gallery-title"><strong>AgentOrdinary colors</strong><small>Select a color for this agent's robot</small></div>
              <div className="character-robot-color-grid">
              {robotColorChoices.map(choice=><button type="button" key={choice.name} className={'character-robot-choice '+((appearance.accentColor||'Green')===choice.name?'selected':'')} aria-pressed={(appearance.accentColor||'Green')===choice.name} onClick={()=>setAppearance(current=>({...current,accentColor:choice.name}))}>
                <span className="character-robot-color-thumb" style={{borderColor:choice.css}}>
                  <img src={(appearance.gender==='Feminine'?'/character-assets/default-agent-female.webp':'/character-assets/default-agent-male.webp')} style={{filter:choice.filter}} alt="" loading="lazy"/>
                </span>
                <span>{choice.name}</span>
              </button>)}
              </div>
            </div>
          )}

                  </div>
                )}

                <div className="builder-block">
                  <div>
                    <h3>Appearance & accessories</h3>
                    <p className="muted">
                      {baseType === 'library_animal'
                        ? "Build the active animal look and accessories. Visual gender choices stay hidden until that animal has genuinely distinct art."
                        : "Build the active layered look, including gender presentation and pronouns. These choices are stored only on this agent's visual profile."}
                    </p>
                  </div>

                  <div className="appearance-grid">
                    {fields.map(field => (
                      <label className="appearance-field" key={field.key}>
                        <span>
                          <strong>{field.label}</strong>
                          <small>{field.note}</small>
                        </span>
                        <select
                          value={baseType==='library_animal' && appearance.species==='Owl' && ['outfit','eyewear','headwear','audio','jewelry','prop'].includes(field.key) ? '' : String(appearance[field.key] ?? field.options[0])}
                          disabled={baseType==='library_animal' && appearance.species==='Owl' && ['outfit','eyewear','headwear','audio','jewelry','prop'].includes(field.key)}
                          onChange={event => setLayer(field.key, event.target.value)}
                          aria-label={field.label}
                        >
                          {baseType==='library_animal' && appearance.species==='Owl' && ['outfit','eyewear','headwear','audio','jewelry','prop'].includes(field.key) && <option value="">Not available yet</option>}
                          {field.options.map(option => (
                            <option value={option} key={option || 'unset'}>
                              {option || 'Not selected'}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </div>

                  {baseType === 'library_animal' && appearance.species === 'Owl' && (
                    <div className="character-booky-actions" aria-label="Owl future accessories">
                      <strong>Owl accessories</strong>
                      <div className="character-names">
                        {['Shirts','Glasses','Hats'].map(label=><label key={label}>{label}
                          <select disabled aria-label={label+' unavailable'}>
                            <option value="">Not available yet</option>
                          </select>
                        </label>)}
                      </div>
                    </div>
                  )}

                  {baseType === 'bookworm' && (
                    <div className="character-booky-actions">
                      <label className="character-auto-glasses">
                        <input type="checkbox" checked={Boolean(appearance.autoWorkGlasses)} onChange={event=>setAppearance(current=>({...current,autoWorkGlasses:event.target.checked}))}/>
                        <span>Automatic glasses: working on, idle off</span>
                      </label>
                      <div className="character-work-preview"><span>Preview</span>
                        <button type="button" className={previewActivity==='idle'?'selected':''} onClick={()=>setPreviewActivity('idle')}>Idle</button>
                        <button type="button" className={previewActivity==='working'?'selected':''} onClick={()=>setPreviewActivity('working')}>Working</button>
                      </div>
                    </div>
                  )}
                  <label className="appearance-field animation-speed-field">
                    <span>
                      <strong>Animation speed</strong>
                      <small>Adjusts visual motion only. It never changes how fast the employee works.</small>
                    </span>
                    <div className="animation-speed-control">
                      <input
                        type="range"
                        min="0.75"
                        max="1.25"
                        step="0.05"
                        value={appearance.animationSpeed ?? 1}
                        onChange={event => {
                          setAppearance(current => ({
                            ...current,
                            animationSpeed: Number(event.target.value),
                          }));
                          setNotice('');
                        }}
                        aria-label="Animation speed"
                      />
                      <strong>{(appearance.animationSpeed ?? 1).toFixed(2)}x</strong>
                    </div>
                  </label>
                </div>

                <details className="character-nickname-panel character-identity-details">
                  <summary>Identity & optional nickname</summary>
                  <div className="character-names">
                    <label>Display name <input value={selectedAgent.title} readOnly aria-label="Display name" /></label>
                  </div>
                  <div className="character-nickname-heading">
                    <div><strong>Nickname (optional)</strong><small>Optional. Leave empty to display the job title.</small></div>
                    <span>{nickname.length}/40</span>
                  </div>
                  <div className="character-nickname-controls">
                    <input aria-label="Agent nickname" value={nickname} maxLength={40} autoComplete="off" placeholder="Optional nickname" onChange={event=>setNickname(event.target.value)} />
                    <button className="primary-action" type="button" disabled={savingNickname||nickname.trim()===(selectedAgent.nickname||'')} onClick={()=>void saveNickname()}>
                      <Save size={16}/>{savingNickname?'Saving...':'Save nickname'}
                    </button>
                  </div>
                </details>
                <div className="save-row">
                  <span className="muted">
                    {dirty
                      ? 'Character look has unsaved changes'
                      : 'Character look saved to AgentOrdinary'}
                  </span>
                  <button className="primary-action" disabled={!dirty || saving} onClick={save}>
                    <Save size={16} />
                    {saving
                      ? 'Saving...'
                      : dirty
                        ? (savedProfile ? 'Save character look' : 'Create character look')
                        : 'Saved'}
                  </button>
                </div>
              </div>

              <aside className="studio-preview-pane" aria-label="Live character preview">
                <div className="preview-pane-heading">
                  <span className="eyebrow">LIVE PREVIEW</span>
                  <strong>Updates instantly</strong>
                </div>
                <CharacterPreviewBoundary key={selectedAgent.id}>
                  <CharacterPreview
                    baseType={baseType}
                    appearance={appearance}
                    agent={previewOnly?{...selectedAgent,status:previewActivity}:selectedAgent}
                    previewOnly={previewOnly}
                  />
                </CharacterPreviewBoundary>
                <small className="preview-help">
                  Appearance changes render locally and do not affect the agent until you save.
                </small>
              </aside>
            </div>

            {baseType === 'library_animal' && appearance.species === 'Owl' && (
              <OwlApprovalSheet />
            )}
          </section>
        </div>
      )}
    </div>
  );
}

